import { WagmiProvider, createConfig, http, type Config } from "wagmi";
import { mainnet } from "wagmi/chains";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { initWallet } from "@/lib/wallet";
import { useAccount } from "wagmi";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { reportWalletConnect } from "@/lib/signed-actions";
import {
  clearAllAuthAndWalletSessions,
  consumePendingWalletRedirect,
  isSessionVerificationError,
} from "@/lib/session-cleanup";
import {
  clearWalletErrors,
  formatWalletDiagnosticsForClipboard,
  getLastWalletError,
  getWalletErrorLog,
  reportWalletError,
  subscribeWalletErrors,
  type WalletDiagnostic,
} from "@/lib/wallet-diagnostics";
import { WalletErrorListener } from "@/components/WalletErrorListener";

const fallbackConfig = createConfig({
  chains: [mainnet],
  transports: { [mainnet.id]: http() },
  ssr: true,
});

const WalletRuntimeContext = createContext<{ ready: boolean; error: WalletDiagnostic | null }>({ ready: false, error: null });

export function useWalletRuntime() {
  return useContext(WalletRuntimeContext);
}

// Paths that should NOT auto-redirect on connect (mid-flow work surfaces).
const NO_REDIRECT_PREFIXES = ["/dashboard", "/admincontrol2026", "/drop", "/market", "/activity", "/collections", "/create"];

function shouldRedirect(pathname: string) {
  return !NO_REDIRECT_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function WalletTracker({ onVerified }: { onVerified: () => void }) {
  const { address, isConnected } = useAccount();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const wasConnected = useRef(false);
  const failedVerificationRef = useRef(false);
  const verifiedAddressRef = useRef<string | null>(null);
  const verifyRunRef = useRef(0);
  const pathRef = useRef(pathname);
  useEffect(() => { pathRef.current = pathname; }, [pathname]);

  const goDashboard = () => {
    import("@/lib/wallet")
      .then((m) => m.closeAppKit && m.closeAppKit())
      .catch(() => undefined);
    if (shouldRedirect(pathRef.current)) {
      router.navigate({ to: "/dashboard" });
    }
  };

  useEffect(() => subscribeWalletErrors((diagnostic) => {
    if (isSessionVerificationError(diagnostic)) {
      failedVerificationRef.current = true;
      verifiedAddressRef.current = null;
      void clearAllAuthAndWalletSessions();
    }
  }), []);

  useEffect(() => {
    const runId = verifyRunRef.current + 1;
    verifyRunRef.current = runId;
    if (isConnected && address) {
      const fresh = !wasConnected.current;
      wasConnected.current = true;
      if (fresh) failedVerificationRef.current = false;
      const shouldGoDashboard = fresh || consumePendingWalletRedirect();
      if (verifiedAddressRef.current === address.toLowerCase()) return;
      reportWalletConnect(address)
        .then(() => {
          if (verifyRunRef.current !== runId || !isConnected || failedVerificationRef.current) return;
          verifiedAddressRef.current = address.toLowerCase();
          clearWalletErrors();
          onVerified();
          if (shouldGoDashboard) goDashboard();
        })
        .catch((error) => {
          if (isSessionVerificationError(error)) {
            failedVerificationRef.current = true;
            void clearAllAuthAndWalletSessions();
          }
          console.warn("wallet track failed", error);
        });
    } else {
      wasConnected.current = false;
      verifiedAddressRef.current = null;
    }
  }, [address, isConnected]);

  return null;
}

function WalletDiagnosticsPanel({ error, onClear }: { error: WalletDiagnostic; onClear: () => void }) {
  const [copied, setCopied] = useState(false);
  const copyLog = async () => {
    const text = formatWalletDiagnosticsForClipboard(getWalletErrorLog());
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <aside className="fixed bottom-4 left-4 right-4 z-[10000] mx-auto max-w-xl rounded-md border border-destructive/50 bg-background/95 p-3 text-left text-xs text-foreground shadow-2xl backdrop-blur md:left-auto md:right-4 md:mx-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-destructive">Wallet connection error</p>
          <p className="mt-1 break-words text-muted-foreground">{error.context ?? "wallet"}: {error.message}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={copyLog} className="rounded border border-border px-2 py-1 hover:bg-muted">
            {copied ? "Copied" : "Copy log"}
          </button>
          <button type="button" onClick={onClear} className="rounded border border-border px-2 py-1 hover:bg-muted">
            Hide
          </button>
        </div>
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer text-muted-foreground">Show details to paste here</summary>
        <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-[11px] leading-relaxed">
          {formatWalletDiagnosticsForClipboard(getWalletErrorLog())}
        </pre>
      </details>
    </aside>
  );
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Config | null>(null);
  const [error, setError] = useState<WalletDiagnostic | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(getLastWalletError());
    const unsubscribe = subscribeWalletErrors((diagnostic) => {
      if (!cancelled) setError(diagnostic);
      if (isSessionVerificationError(diagnostic)) void clearAllAuthAndWalletSessions();
    });
    const onWindowError = (event: ErrorEvent) => {
      const message = event.message || event.error?.message || "Wallet error";
      if (/WalletConnect|W3MFrame|So\.default|connector|Session verification failed|Signature verification failed|Forbidden: Session/i.test(message)) {
        reportWalletError(event.error ?? new Error(message), { context: "window.error" });
      }
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as { message?: string } | undefined;
      if (/WalletConnect|W3MFrame|So\.default|connector|Session verification failed|Signature verification failed|Forbidden: Session/i.test(reason?.message ?? String(event.reason))) {
        reportWalletError(event.reason, { context: "unhandledrejection" });
      }
    };
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    initWallet()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((err) => console.error("initWallet failed", err));
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return (
    <WagmiProvider config={config ?? fallbackConfig} reconnectOnMount={!!config}>
      <WalletRuntimeContext.Provider value={{ ready: !!config, error }}>
        {config && <WalletTracker onVerified={() => setError(null)} />}
        {config && <WalletErrorListener />}
        {children}
        {error && (
          <WalletDiagnosticsPanel
            error={error}
            onClear={() => {
              clearWalletErrors();
              setError(null);
            }}
          />
        )}
      </WalletRuntimeContext.Provider>
    </WagmiProvider>
  );
}
